import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL, patientProfileUrl, therapistActivityPlanUrl } from "../../config/constants";
import AddPatientModal from "../../components/addPatientModal";
import AppSidebar from "../../components/AppSidebar";
import avatarMap from "../../assets/avatars";
import {
  Layout, Main, TopBar, AddButton, SearchWrapper, SearchIcon, SearchInput,
  Content, PageTitle,
  Table, Thead, Th, Tbody, Tr, Td, AvatarCell, EmptyRow,
  ActionCell, IconBtn,
} from "./styles";

const calcAgeMonths = (patient) => {
  if (patient.age_at_start_corrected) return patient.age_at_start_corrected;
  const baseDate = patient.is_premature
    ? patient.expected_delivery_date
    : patient.date_of_birth;
  if (!baseDate || !patient.treatment_start_date) return "—";
  const base = new Date(baseDate);
  const start = new Date(patient.treatment_start_date);
  const months =
    (start.getFullYear() - base.getFullYear()) * 12 +
    (start.getMonth() - base.getMonth());
  return months > 0 ? `${months} months` : "—";
};

const IconProfile = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconActivityPlan = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconSummary = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconCaregiverSupport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const TherapistHome = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const token = localStorage.getItem("token");

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchPatients = () => {
    fetch(`${API_URL}/patients`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { setPatients(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return patients;
    const q = search.toLowerCase();
    return patients.filter(
      (p) =>
        p.patient_code?.toLowerCase().includes(q) ||
        p.username?.toLowerCase().includes(q) ||
        p.pathology?.toLowerCase().includes(q)
    );
  }, [patients, search]);

  const handleProfile = (patient, e) => {
    e.stopPropagation();
    history.push(patientProfileUrl(patient.id), { patient });
  };

  const handleActivityPlan = (patient, e) => {
    e.stopPropagation();
    history.push(therapistActivityPlanUrl(patient.id), { patient });
  };

  return (
    <Layout>
      <AppSidebar patient={selectedPatient} activeNav="patients" />

      <Main>
        <TopBar>
          <AddButton onClick={() => setShowAddModal(true)}>
            {t("patients.addNew")}
          </AddButton>
          <SearchWrapper>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              placeholder={t("patients.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </TopBar>

        <Content>
          <PageTitle>{t("patients.title")}</PageTitle>
          <Table>
            <Thead>
              <tr>
                <Th style={{ width: 40 }} />
                <Th>{t("patients.table.patientId")}</Th>
                <Th>{t("patients.table.cohort")}</Th>
                <Th>{t("patients.table.age")}</Th>
                <Th>{t("patients.table.sex")}</Th>
                <Th>{t("patients.table.pathology")}</Th>
                <Th>{t("patients.table.actions")}</Th>
              </tr>
            </Thead>
            <Tbody>
              {loading ? (
                <EmptyRow><td colSpan={7}>{t("patients.loading")}</td></EmptyRow>
              ) : filtered.length === 0 ? (
                <EmptyRow><td colSpan={7}>{t("patients.empty")}</td></EmptyRow>
              ) : (
                filtered.map((p) => (
                  <Tr
                    key={p.id}
                    $selected={selectedPatient?.id === p.id}
                    onClick={() => setSelectedPatient(p)}
                  >
                    <Td>
                      <AvatarCell
                        src={avatarMap[p.avatar_url] || Object.values(avatarMap)[0]}
                        alt="avatar"
                      />
                    </Td>
                    <Td>{p.patient_code || "—"}</Td>
                    <Td>{p.cohort ?? "—"}</Td>
                    <Td>{calcAgeMonths(p)}</Td>
                    <Td>{p.sex || "—"}</Td>
                    <Td>{p.pathology || "—"}</Td>
                    <Td>
                      <ActionCell>
                        <IconBtn title={t("nav.profile")} onClick={(e) => handleProfile(p, e)}>
                          <IconProfile />
                        </IconBtn>
                        <IconBtn title={t("nav.activityPlan")} onClick={(e) => handleActivityPlan(p, e)}>
                          <IconActivityPlan />
                        </IconBtn>
                        <IconBtn title={t("nav.summary")} onClick={(e) => e.stopPropagation()}>
                          <IconSummary />
                        </IconBtn>
                        <IconBtn title={t("nav.caregiverSupport")} onClick={(e) => e.stopPropagation()}>
                          <IconCaregiverSupport />
                        </IconBtn>
                      </ActionCell>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Content>
      </Main>

      {showAddModal && (
        <AddPatientModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchPatients(); }}
        />
      )}
    </Layout>
  );
};

export default TherapistHome;
